import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Item } from "../../../api/menu";
import { isLunchCategoryId, sourceIdOfLunchItemId } from "../../../lib/lunchCatalog";
import type { useOrderingMessage } from "../components/useOrderingMessage";

interface CartLine {
  itemId: number;
}

interface EndedLunchCartOptions<T extends CartLine> {
  allItems: Item[];
  orderItems: T[];
  setOrderItems: (items: T[]) => void;
  selectedCategoryId: number | null;
  setSelectedCategoryId: (categoryId: number | null) => void;
  message: ReturnType<typeof useOrderingMessage>;
}

/**
 * Обедното меню свърши (или е сменено/изтрито), докато в кошницата има негов
 * продукт: копието изчезва от каталога и поръчката би гръмнала. Редът минава
 * на основния продукт с основната цена; ако и него го няма — отпада.
 * Избраната обедна група, която вече я няма, се нулира (иначе празен каталог).
 */
export const useEndedLunchCartItems = <T extends CartLine>({
  allItems,
  orderItems,
  setOrderItems,
  selectedCategoryId,
  setSelectedCategoryId,
  message,
}: EndedLunchCartOptions<T>) => {
  const { t } = useTranslation();
  // Само при смяна на каталога — не при всяко добавяне в кошницата
  const lastCatalog = useRef(allItems);

  useEffect(() => {
    if (lastCatalog.current === allItems) return;
    lastCatalog.current = allItems;
    // Празен каталог = зарежда се/грешка — не пипаме кошницата
    if (allItems.length === 0) return;
    const itemIds = new Set(allItems.map((item) => item.id));

    let switched = 0;
    let removed = 0;
    const next = orderItems.flatMap((line) => {
      if (itemIds.has(line.itemId) || line.itemId >= 0) return [line];
      const sourceId = sourceIdOfLunchItemId(line.itemId);
      if (itemIds.has(sourceId)) {
        switched += 1;
        return [{ ...line, itemId: sourceId }];
      }
      removed += 1;
      return [];
    });
    if (switched > 0 || removed > 0) {
      setOrderItems(next);
      if (switched > 0) message.warning(t("lunchMenu.cartSwitchedToRegular", { count: switched }));
      if (removed > 0) message.warning(t("lunchMenu.cartRemoved", { count: removed }));
    }

    if (
      selectedCategoryId != null &&
      isLunchCategoryId(selectedCategoryId) &&
      !allItems.some((item) => item.categoryId === selectedCategoryId)
    )
      setSelectedCategoryId(null);
  }, [allItems, orderItems, setOrderItems, selectedCategoryId, setSelectedCategoryId, message, t]);
};
